import { useEffect, useState } from "react";
import { api, adminAuth } from "../../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function DiscountsTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: "" });
  const load = async () => {
    try { const r = await api.get("/admin/discounts", { headers: adminAuth() }); setList(r.data); }
    catch (err) { console.error("Failed to load discounts", err); toast.error("Couldn't load discount codes"); }
  };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/discounts", { ...form, value: parseFloat(form.value) }, { headers: adminAuth() });
      setForm({ code: "", type: "percent", value: "" });
      toast.success("Code created");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const toggle = async (d) => {
    const next = !d.is_active;
    setList((prev) => prev.map((x) => x.id === d.id ? { ...x, is_active: next } : x));
    try {
      await api.patch(`/admin/discounts/${d.id}`, { is_active: next }, { headers: adminAuth() });
      toast.success(next ? `${d.code} enabled` : `${d.code} disabled`);
    } catch (err) {
      setList((prev) => prev.map((x) => x.id === d.id ? { ...x, is_active: d.is_active } : x));
      toast.error("Couldn't update code");
    }
  };
  const remove = async (d) => {
    if (!window.confirm(`Permanently delete code "${d.code}"? This can't be undone.`)) return;
    try { await api.delete(`/admin/discounts/${d.id}`, { headers: adminAuth() }); load(); toast.success("Code deleted"); }
    catch (err) { console.error("Failed to delete discount", err); toast.error("Couldn't delete code"); }
  };
  return (
    <div className="card-rustic" data-testid="admin-discounts">
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]">
        <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--crimson)]">/DISCOUNTS</div>
        <div className="font-serif-display text-2xl text-[var(--navy)]">Discount Codes</div>
        <div className="text-xs text-[var(--ink-soft)] mt-1">Toggle a code on/off to pause it without losing it, or delete it for good.</div>
      </div>
      <form onSubmit={create} className="p-5 border-b border-[rgba(27,42,65,0.06)] grid grid-cols-1 md:grid-cols-4 gap-3">
        <input className="input-field" placeholder="CODE" value={form.code} onChange={(e)=>setForm({...form, code: e.target.value.toUpperCase()})} data-testid="discount-code-input"/>
        <select className="input-field" value={form.type} onChange={(e)=>setForm({...form, type: e.target.value})} data-testid="discount-type-select">
          <option value="percent">Percent %</option>
          <option value="fixed">Fixed $</option>
        </select>
        <input className="input-field" placeholder={form.type === "percent" ? "10 (for 10%)" : "5.00"} type="number" step="0.01" value={form.value} onChange={(e)=>setForm({...form, value: e.target.value})} data-testid="discount-value-input"/>
        <button className="btn-primary justify-center" data-testid="discount-create-button">Create</button>
      </form>
      <div className="divide-y divide-[rgba(27,42,65,0.06)]">
        {list.map((d) => (
          <div key={d.id} className="p-4 flex items-center justify-between gap-4" data-testid={`discount-row-${d.id}`}>
            <div>
              <div className="font-mono-ui text-sm text-[var(--navy)] flex items-center gap-2">
                {d.code}
                <span className={`inline-block px-2 py-0.5 rounded-full font-mono-ui text-[9px] tracking-[0.15em] uppercase ${d.is_active ? "bg-[var(--gold)]/25 text-[var(--navy)]" : "bg-[var(--ink-soft)]/15 text-[var(--ink-soft)]"}`} data-testid={`discount-status-${d.id}`}>
                  {d.is_active ? "Active" : "Disabled"}
                </span>
              </div>
              <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--ink-soft)] mt-1">{d.type === "percent" ? `${d.value}% off` : `$${d.value} off`}</div>
              <div className="font-mono-ui text-[10px] tracking-[0.15em] text-[var(--navy)]/70 mt-1" data-testid={`discount-usage-${d.id}`}>
                {d.usage_count > 0
                  ? <>Used <span className="text-[var(--crimson)] font-bold">{d.usage_count}×</span>{d.total_saved > 0 ? ` · $${d.total_saved.toFixed(2)} given` : ""}</>
                  : "Not used yet"}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                role="switch"
                aria-checked={d.is_active}
                onClick={() => toggle(d)}
                data-testid={`discount-toggle-${d.id}`}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${d.is_active ? "bg-[var(--crimson)]" : "bg-[var(--ink-soft)]/40"}`}
                title={d.is_active ? "Disable code" : "Enable code"}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${d.is_active ? "translate-x-6" : "translate-x-1"}`}/>
              </button>
              <button
                type="button"
                onClick={() => remove(d)}
                data-testid={`discount-delete-${d.id}`}
                className="inline-flex items-center gap-1.5 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 px-2 py-1.5 rounded-sm font-mono-ui text-[10px] tracking-[0.2em] uppercase transition"
                title="Delete permanently"
              >
                <Trash2 className="w-3.5 h-3.5"/> Delete
              </button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="p-8 text-center text-[var(--ink-soft)] text-sm">No codes yet.</div>}
      </div>
    </div>
  );
}
